import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  Squares2X2Icon,
  TableCellsIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  ChartBarIcon,
  BanknotesIcon,
  CreditCardIcon,
  BuildingLibraryIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalculatorIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { formatCurrency, safeNumber } from '../utils/formatters';
import PageHero from '../components/ui/PageHero';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../components/ui/BrandModal';
import { accountsApi, Account, AccountType, AccountSubType, CreateAccountDto, UpdateAccountDto } from '../api/accounts';
import { journalApi, JournalEntry, CreateJournalEntryDto, UpdateJournalEntryDto } from '../api/journal';
import { STANDARD_CHART_OF_ACCOUNTS } from '../content/defaultAccounting';
import AccountModal from '../components/accounting/AccountModal';
import JournalEntryModal from '../components/accounting/JournalEntryModal';
import AccountingReportsPanel from '../components/accounting/AccountingReportsPanel';
import CostCentersPanel from '../components/accounting/CostCentersPanel';
import FinancialReportsPanel from '../components/accounting/FinancialReportsPanel';

type AccountingTab = 'accounts' | 'journal' | 'reports' | 'cost-centers' | 'saved-reports';

interface AccountingFilters {
  accountType?: AccountType;
  accountCategory?: AccountSubType;
  currency?: string;
  isActive?: boolean;
  dateRange?: {
    start: string;
    end: string;
  };
  balanceRange?: {
    min: number;
    max: number;
  };
  hasTransactions?: boolean;
}

interface JournalFilters {
  status?: JournalEntry['status'];
  source?: JournalEntry['source'];
  dateRange?: {
    start: string;
    end: string;
  };
  amountRange?: {
    min: number;
    max: number;
  };
  accountId?: string;
}

export default function Accounting() {
  // Data state
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [journalApiAvailable, setJournalApiAvailable] = useState(true);

  // UI state
  const [activeTab, setActiveTab] = useState<AccountingTab>('accounts');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<AccountingFilters | JournalFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [sortBy, setSortBy] = useState<string>('code');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');

  // Modal state
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showJournalModal, setShowJournalModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [editingJournalEntry, setEditingJournalEntry] = useState<JournalEntry | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [selectedJournalEntry, setSelectedJournalEntry] = useState<JournalEntry | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [seedingAccounts, setSeedingAccounts] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load accounts and journal entries
      const [accountsData, journalData] = await Promise.all([
        accountsApi.getAccounts().catch((error) => {
          return [];
        }),
        journalApi.getJournalEntries().catch(() => []),
      ]);
      
      setAccounts(accountsData);
      setJournalEntries(journalData);
      
      if (accountsData.length > 0) {
        toast.success(`Loaded ${accountsData.length} accounts and ${journalData.length} journal entries`);
      } else {
        toast('No accounting data found - create your first account to get started', {
          icon: '📊',
        });
      }
    } catch (err: any) {
      console.error('Error loading accounting data:', err);
      setError('Failed to load accounting data');
      toast.error('Failed to load accounting data');
    } finally {
      setLoading(false);
    }
  };

  const handlePopulateStandardChart = async () => {
    setSeedingAccounts(true);
    try {
      const seeded = await accountsApi.seedDefaultAccounts();
      let created = seeded?.created ?? 0;
      if (seeded == null) {
        const existing = await accountsApi.getAccounts().catch(() => [] as Account[]);
        const have = new Set(existing.map((a) => a.code));
        for (const row of STANDARD_CHART_OF_ACCOUNTS) {
          if (have.has(row.code)) continue;
          await accountsApi.createAccount({ ...row, isActive: true, balance: 0 });
          created += 1;
        }
      }
      await loadData();
      toast.success(created > 0 ? `Added ${created} standard accounts` : 'Chart of accounts is already populated');
    } catch (err) {
      console.error('Error seeding chart of accounts:', err);
      toast.error('Could not populate the chart of accounts');
    } finally {
      setSeedingAccounts(false);
    }
  };

  // Account CRUD operations
  const handleCreateAccount = async (data: CreateAccountDto) => {
    setModalLoading(true);
    try {
      const newAccount = await accountsApi.createAccount(data);
      setAccounts(prev => [...prev, newAccount]);
      toast.success('Account created successfully');
    } catch (error: any) {
      console.error('Error creating account:', error);
      throw new Error(error.response?.data?.message || 'Failed to create account');
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdateAccount = async (data: UpdateAccountDto) => {
    if (!editingAccount) return;
    
    setModalLoading(true);
    try {
      const updatedAccount = await accountsApi.updateAccount(editingAccount.id, data);
      setAccounts(prev => prev.map(acc => acc.id === editingAccount.id ? updatedAccount : acc));
      toast.success('Account updated successfully');
    } catch (error: any) {
      console.error('Error updating account:', error);
      throw new Error(error.response?.data?.message || 'Failed to update account');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!selectedAccount) return;
    
    try {
      await accountsApi.deleteAccount(selectedAccount.id);
      setAccounts(prev => prev.filter(acc => acc.id !== selectedAccount.id));
      setShowDeleteModal(false);
      setSelectedAccount(null);
      toast.success('Account deleted successfully');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      toast.error(error.response?.data?.message || 'Failed to delete account');
    }
  };

  // Journal Entry CRUD operations
  const handleCreateJournalEntry = async (data: CreateJournalEntryDto) => {
    setModalLoading(true);
    try {
      const newEntry = await journalApi.createJournalEntry(data);
      setJournalEntries(prev => [...prev, newEntry]);
      toast.success('Journal entry created successfully');
    } catch (error: any) {
      console.error('Error creating journal entry:', error);
      toast.error(error.response?.data?.message || 'Failed to create journal entry. Please try again.');
      throw new Error(error.response?.data?.message || 'Failed to create journal entry');
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdateJournalEntry = async (data: UpdateJournalEntryDto) => {
    if (!editingJournalEntry) return;
    
    setModalLoading(true);
    try {
      const updatedEntry = await journalApi.updateJournalEntry(editingJournalEntry.id, data);
      setJournalEntries(prev => prev.map(entry => entry.id === editingJournalEntry.id ? updatedEntry : entry));
      toast.success('Journal entry updated successfully');
    } catch (error: any) {
      console.error('Error updating journal entry:', error);
      toast.error(error.response?.data?.message || 'Failed to update journal entry. Please try again.');
      throw new Error(error.response?.data?.message || 'Failed to update journal entry');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteJournalEntry = async () => {
    if (!selectedJournalEntry) return;
    
    try {
      await journalApi.deleteJournalEntry(selectedJournalEntry.id);
      setJournalEntries(prev => prev.filter(entry => entry.id !== selectedJournalEntry.id));
      setShowDeleteModal(false);
      setSelectedJournalEntry(null);
      toast.success('Journal entry deleted successfully');
    } catch (error: any) {
      console.error('Error deleting journal entry:', error);
      toast.error(error.response?.data?.message || 'Failed to delete journal entry. Please try again.');
    }
  };

  const handleApproveJournalEntry = async (id: string) => {
    try {
      const approvedEntry = await journalApi.postJournalEntry(id);
      setJournalEntries(prev => prev.map(entry => entry.id === id ? approvedEntry : entry));
      toast.success('Journal entry approved and posted successfully');
    } catch (error: any) {
      console.error('Error approving journal entry:', error);
      toast.error(error.response?.data?.message || 'Failed to approve journal entry. Please try again.');
      throw new Error(error.response?.data?.message || 'Failed to approve journal entry');
    }
  };

  const getNextEntryNumber = async (): Promise<string> => {
    try {
      const { entryNumber } = await journalApi.getNextEntryNumber();
      return entryNumber;
    } catch (error: any) {
      // Generate a local entry number if API is not available
      const currentYear = new Date().getFullYear();
      const nextNumber = journalEntries.length + 1;
      return `JE-${currentYear}-${String(nextNumber).padStart(3, '0')}`;
    }
  };

  // Filter and search logic for accounts
  const filteredAccounts = useMemo(() => {
    if (activeTab !== 'accounts') return [];
    
    let result = accounts;
    const accountFilters = filters as AccountingFilters;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (account) =>
          account.code.toLowerCase().includes(term) ||
          account.name.toLowerCase().includes(term) ||
          account.description?.toLowerCase().includes(term)
      );
    }

    // Apply filters
    if (accountFilters.accountType) {
      result = result.filter((account) => account.type === accountFilters.accountType);
    }
    if (accountFilters.accountCategory) {
      result = result.filter((account) => account.subType === accountFilters.accountCategory);
    }
    if (accountFilters.isActive !== undefined) {
      result = result.filter((account) => account.isActive === accountFilters.isActive);
    }
    if (accountFilters.balanceRange) {
      result = result.filter((account) => 
        account.balance >= accountFilters.balanceRange!.min && 
        account.balance <= accountFilters.balanceRange!.max
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal: any = a[sortBy as keyof Account];
      let bVal: any = b[sortBy as keyof Account];
      
      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return sortOrder === 'asc' ? 1 : -1;
      if (bVal === undefined) return sortOrder === 'asc' ? -1 : 1;
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [accounts, searchTerm, filters, sortBy, sortOrder, activeTab]);

  // Filter and search logic for journal entries
  const filteredJournalEntries = useMemo(() => {
    if (activeTab !== 'journal') return [];
    
    let result = journalEntries;
    const journalFilters = filters as JournalFilters;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (entry) =>
          entry.entryNumber.toLowerCase().includes(term) ||
          entry.description.toLowerCase().includes(term) ||
          entry.reference?.toLowerCase().includes(term) ||
          entry.notes?.toLowerCase().includes(term) ||
          entry.lineItems.some(line => 
            line.accountName.toLowerCase().includes(term) ||
            line.description?.toLowerCase().includes(term)
          )
      );
    }

    // Apply filters
    if (journalFilters.status) {
      result = result.filter((entry) => entry.status === journalFilters.status);
    }
    if (journalFilters.source) {
      result = result.filter((entry) => entry.source === journalFilters.source);
    }
    if (journalFilters.accountId) {
      result = result.filter((entry) => 
        entry.lineItems.some(line => line.accountId === journalFilters.accountId)
      );
    }
    if (journalFilters.dateRange) {
      result = result.filter((entry) => {
        const entryDate = new Date(entry.date);
        const startDate = new Date(journalFilters.dateRange!.start);
        const endDate = new Date(journalFilters.dateRange!.end);
        return entryDate >= startDate && entryDate <= endDate;
      });
    }
    if (journalFilters.amountRange) {
      result = result.filter((entry) => 
        entry.totalDebit >= journalFilters.amountRange!.min && 
        entry.totalDebit <= journalFilters.amountRange!.max
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal: any = a[sortBy as keyof JournalEntry];
      let bVal: any = b[sortBy as keyof JournalEntry];
      
      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return sortOrder === 'asc' ? 1 : -1;
      if (bVal === undefined) return sortOrder === 'asc' ? -1 : 1;
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [journalEntries, searchTerm, filters, sortBy, sortOrder, activeTab]);

  // Get current filtered data
  const currentData = activeTab === 'accounts' ? filteredAccounts : filteredJournalEntries;

  // Pagination logic
  const totalPages = Math.ceil(currentData.length / itemsPerPage);
  const paginatedData = currentData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Event handlers
  const handleAddNew = () => {
    if (activeTab === 'accounts') {
      setEditingAccount(null);
      setShowAccountModal(true);
    } else if (activeTab === 'journal') {
      setEditingJournalEntry(null);
      setShowJournalModal(true);
    }
  };

  const handleEdit = (item: Account | JournalEntry) => {
    if (activeTab === 'accounts') {
      setEditingAccount(item as Account);
      setShowAccountModal(true);
    } else {
      setEditingJournalEntry(item as JournalEntry);
      setShowJournalModal(true);
    }
  };

  const handleView = (item: Account | JournalEntry) => {
    if (activeTab === 'accounts') {
      setSelectedAccount(item as Account);
    } else {
      setSelectedJournalEntry(item as JournalEntry);
    }
    setShowViewModal(true);
  };

  const handleDelete = (item: Account | JournalEntry) => {
    if (activeTab === 'accounts') {
      setSelectedAccount(item as Account);
    } else {
      setSelectedJournalEntry(item as JournalEntry);
    }
    setShowDeleteModal(true);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  // Helper functions
  const getAccountTypeBadge = (account: Account) => {
    const typeConfig = {
      ASSET: { color: 'bg-emerald-100 text-emerald-800', icon: BanknotesIcon },
      LIABILITY: { color: 'bg-rose-100 text-rose-800', icon: CreditCardIcon },
      EQUITY: { color: 'bg-cyan-100 text-cyan-800', icon: BuildingLibraryIcon },
      REVENUE: { color: 'bg-emerald-100 text-emerald-800', icon: ArrowTrendingUpIcon },
      EXPENSE: { color: 'bg-orange-100 text-orange-800', icon: ArrowTrendingDownIcon },
    };

    const config = typeConfig[account.type];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {account.type.charAt(0).toUpperCase() + account.type.slice(1).toLowerCase()}
      </span>
    );
  };

  const getStatusBadge = (entry: JournalEntry) => {
    const statusConfig = {
      draft: { color: 'bg-violet-100 text-violet-800', icon: DocumentTextIcon },
      posted: { color: 'bg-emerald-100 text-emerald-800', icon: CalculatorIcon },
      reversed: { color: 'bg-rose-100 text-rose-800', icon: XMarkIcon },
    };

    const config = statusConfig[entry.status];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
      </span>
    );
  };

  const getSourceBadge = (entry: JournalEntry) => {
    const sourceConfig = {
      manual: { color: 'bg-violet-100 text-violet-800' },
      sales: { color: 'bg-emerald-100 text-emerald-800' },
      purchase: { color: 'bg-cyan-100 text-cyan-800' },
      payment: { color: 'bg-purple-100 text-purple-800' },
      system: { color: 'bg-cyan-100 text-cyan-800' },
    };

    const config = sourceConfig[entry.source];

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {entry.source.charAt(0).toUpperCase() + entry.source.slice(1)}
      </span>
    );
  };

  // Financial calculations
  const totalAssets = accounts
    .filter(a => a.type === 'ASSET')
    .reduce((sum, account) => sum + safeNumber(account.balance), 0);
  const totalLiabilities = accounts
    .filter(a => a.type === 'LIABILITY')
    .reduce((sum, account) => sum + safeNumber(account.balance), 0);
  const totalEquity = accounts
    .filter(a => a.type === 'EQUITY')
    .reduce((sum, account) => sum + safeNumber(account.balance), 0);
  const totalRevenue = accounts
    .filter(a => a.type === 'REVENUE')
    .reduce((sum, account) => sum + safeNumber(account.balance), 0);
  const totalExpenses = accounts
    .filter(a => a.type === 'EXPENSE')
    .reduce((sum, account) => sum + safeNumber(account.balance), 0);
  const netIncome = totalRevenue - totalExpenses;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h3 className="mt-2 text-sm font-semibold text-slate-900">Error</h3>
          <p className="mt-1 text-sm text-slate-500">{error}</p>
          <div className="mt-6">
            <button
              type="button"
              className="inline-flex items-center btn btn-primary text-sm shadow-sm"
              onClick={loadData}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <PageHero
        title="Accounting"
        subtitle="Chart of accounts, journal entries, and financial reporting"
        actions={
          (activeTab === 'accounts' || activeTab === 'journal') ? (
            <button type="button" onClick={handleAddNew} className="btn btn-primary focus-pop">
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              {activeTab === 'accounts' ? 'Add Account' : 'New Journal Entry'}
            </button>
          ) : undefined
        }
      />

      {/* Journal Entries are now fully functional with real backend API */}

      {/* Tabs */}
      <div className="mt-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {[
              { id: 'accounts', name: 'Chart of Accounts', icon: ClipboardDocumentListIcon },
              { id: 'journal', name: 'Journal Entries', icon: DocumentTextIcon },
              { id: 'cost-centers', name: 'Cost Centers', icon: BuildingLibraryIcon },
              { id: 'reports', name: 'Analytics', icon: ChartBarIcon },
              { id: 'saved-reports', name: 'Saved Reports', icon: DocumentTextIcon },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id as AccountingTab);
                    setCurrentPage(1);
                    setSearchTerm('');
                    setFilters({});
                  }}
                  className={`${
                    activeTab === tab.id
                      ? 'border-cyan-500 text-cyan-600'
                      : 'border-transparent text-slate-500 hover:border-gray-300 hover:text-slate-700'
                  } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium flex items-center`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Filters and Search */}
      {(activeTab === 'accounts' || activeTab === 'journal') && (
      <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-1 items-center gap-4">
          <div className="flex items-center flex-1 max-w-md rounded-md ring-1 ring-inset ring-gray-300 bg-white focus-within:ring-2 focus-within:ring-inset focus-within:ring-cyan-500">
            <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
              <MagnifyingGlassIcon className="h-5 w-5" />
            </span>
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 min-w-0 py-1.5 pr-3 border-0 bg-transparent text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 ${showFilters ? 'bg-slate-50/80' : ''}`}
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setViewMode(viewMode === 'table' ? 'grid' : 'table')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
          >
            {viewMode === 'table' ? (
              <Squares2X2Icon className="h-4 w-4" />
            ) : (
              <TableCellsIcon className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
      )}

      {/* Filter Panel */}
      {showFilters && (activeTab === 'accounts' || activeTab === 'journal') && (
        <div className="mt-4 bg-slate-50/80 p-4 rounded-lg">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {activeTab === 'accounts' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Account Type</label>
                  <select
                    value={(filters as AccountingFilters).accountType || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, accountType: e.target.value as AccountType || undefined }))}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                  >
                    <option key="empty-option-1753461297353" value="">All Types</option>
                    <option key="ASSET-1753461297353" value="ASSET">Asset</option>
                    <option key="LIABILITY-1753461297353" value="LIABILITY">Liability</option>
                    <option key="EQUITY-1753461297353" value="EQUITY">Equity</option>
                    <option key="REVENUE-1753461297353" value="REVENUE">Revenue</option>
                    <option key="EXPENSE-1753461297353" value="EXPENSE">Expense</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Status</label>
                  <select
                    value={(filters as AccountingFilters).isActive !== undefined ? (filters as AccountingFilters).isActive!.toString() : ''}
                    onChange={(e) => setFilters(prev => ({ 
                      ...prev, 
                      isActive: e.target.value ? e.target.value === 'true' : undefined 
                    }))}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                  >
                    <option key="empty-option-1753461297353" value="">All Status</option>
                    <option key="true-1753461297353" value="true">Active</option>
                    <option key="false-1753461297353" value="false">Inactive</option>
                  </select>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Status</label>
                  <select
                    value={(filters as JournalFilters).status || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as JournalEntry['status'] || undefined }))}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                  >
                    <option key="empty-option-1753461297353" value="">All Status</option>
                    <option key="draft-1753461297353" value="draft">Draft</option>
                    <option key="posted-1753461297353" value="posted">Posted</option>
                    <option key="reversed-1753461297353" value="reversed">Reversed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Source</label>
                  <select
                    value={(filters as JournalFilters).source || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value as JournalEntry['source'] || undefined }))}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                  >
                    <option key="empty-option-1753461297353" value="">All Sources</option>
                    <option key="manual-1753461297353" value="manual">Manual</option>
                    <option key="sales-1753461297353" value="sales">Sales</option>
                    <option key="purchase-1753461297353" value="purchase">Purchase</option>
                    <option key="payment-1753461297353" value="payment">Payment</option>
                    <option key="system-1753461297353" value="system">System</option>
                  </select>
                </div>
              </>
            )}
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm text-cyan-600 hover:text-cyan-500"
            >
              Clear filters
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {activeTab === 'accounts' && (
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BanknotesIcon className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Total Assets</dt>
                    <dd className="text-lg font-medium text-slate-900">{formatCurrency(totalAssets)}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CreditCardIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Total Liabilities</dt>
                    <dd className="text-lg font-medium text-slate-900">{formatCurrency(totalLiabilities)}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BuildingLibraryIcon className="h-6 w-6 text-cyan-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Total Equity</dt>
                    <dd className="text-lg font-medium text-slate-900">{formatCurrency(totalEquity)}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ArrowTrendingUpIcon className="h-6 w-6 text-emerald-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Total Revenue</dt>
                    <dd className="text-lg font-medium text-slate-900">{formatCurrency(totalRevenue)}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CurrencyDollarIcon className={`h-6 w-6 ${netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`} />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Net Income</dt>
                    <dd className={`text-lg font-medium ${netIncome >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                      {formatCurrency(netIncome)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              {activeTab === 'accounts' ? (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-slate-50/80">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => {
                            setSortBy('code');
                            setSortOrder(sortBy === 'code' && sortOrder === 'asc' ? 'desc' : 'asc');
                          }}>
                        Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => {
                            setSortBy('name');
                            setSortOrder(sortBy === 'name' && sortOrder === 'asc' ? 'desc' : 'asc');
                          }}>
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => {
                            setSortBy('balance');
                            setSortOrder(sortBy === 'balance' && sortOrder === 'asc' ? 'desc' : 'asc');
                          }}>
                        Balance
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center">
                          <div className="text-sm text-slate-500">
                            {filteredAccounts.length === 0 && accounts.length === 0 ? (
                              <>
                                <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-slate-400" />
                                <h3 className="mt-2 text-sm font-semibold text-slate-900">No accounts</h3>
                                <p className="mt-1 text-sm text-slate-500">
                                  Load a standard aviary chart of accounts, or create accounts one by one.
                                </p>
                                <button
                                  type="button"
                                  onClick={handlePopulateStandardChart}
                                  disabled={seedingAccounts}
                                  className="btn btn-primary mt-4 text-sm"
                                >
                                  {seedingAccounts ? 'Loading standard accounts…' : 'Load standard chart of accounts'}
                                </button>
                              </>
                            ) : (
                              'No accounts match your search criteria.'
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      (paginatedData as Account[]).map((account) => (
                        <tr key={account.id} className="hover:bg-slate-50/80">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                            {account.code}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-slate-900">{account.name}</div>
                              {account.description && (
                                <div className="text-sm text-slate-500">{account.description}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getAccountTypeBadge(account)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                            <span className={safeNumber(account.balance) >= 0 ? 'text-slate-900' : 'text-red-600'}>
                              {formatCurrency(safeNumber(account.balance))}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              account.isActive 
                                ? 'bg-emerald-100 text-emerald-800' 
                                : 'bg-violet-100 text-violet-800'
                            }`}>
                              {account.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleView(account)}
                                className="text-cyan-600 hover:text-indigo-900"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleEdit(account)}
                                className="text-cyan-600 hover:text-indigo-900"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(account)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              ) : activeTab === 'journal' ? (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-slate-50/80">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => {
                            setSortBy('entryNumber');
                            setSortOrder(sortBy === 'entryNumber' && sortOrder === 'asc' ? 'desc' : 'asc');
                          }}>
                        Entry #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => {
                            setSortBy('date');
                            setSortOrder(sortBy === 'date' && sortOrder === 'asc' ? 'desc' : 'asc');
                          }}>
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Source
                      </th>
                      <th className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center">
                          <div className="text-sm text-slate-500">
                            {filteredJournalEntries.length === 0 && journalEntries.length === 0 ? (
                              <>
                                <DocumentTextIcon className="mx-auto h-12 w-12 text-slate-400" />
                                <h3 className="mt-2 text-sm font-semibold text-slate-900">No journal entries</h3>
                                <p className="mt-1 text-sm text-slate-500">Get started by creating your first journal entry.</p>
                              </>
                            ) : (
                              'No journal entries match your search criteria.'
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      (paginatedData as JournalEntry[]).map((entry) => (
                        <tr key={entry.id} className="hover:bg-slate-50/80">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                            {entry.entryNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                            {format(new Date(entry.date), 'MMM dd, yyyy')}
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <div className="text-sm font-medium text-slate-900">{entry.description}</div>
                              {entry.reference && (
                                <div className="text-sm text-slate-500">Ref: {entry.reference}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-slate-900">
                            {formatCurrency(entry.totalDebit)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {getStatusBadge(entry)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {getSourceBadge(entry)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleView(entry)}
                                className="text-cyan-600 hover:text-indigo-900"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              {entry.status === 'draft' && (
                                <button
                                  onClick={() => handleEdit(entry)}
                                  className="text-cyan-600 hover:text-indigo-900"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                              )}
                              {entry.status === 'draft' && (
                                <button
                                  onClick={() => handleDelete(entry)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              ) : activeTab === 'cost-centers' ? (
                <CostCentersPanel />
              ) : activeTab === 'saved-reports' ? (
                <FinancialReportsPanel />
              ) : (
                <AccountingReportsPanel />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-700">
                Showing <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, currentData.length)}
                </span>{' '}
                of <span className="font-medium">{currentData.length}</span> results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                      pageNumber === currentPage
                        ? 'z-10 bg-cyan-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600'
                        : 'text-slate-900 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0'
                    }`}
                  >
                    {pageNumber}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <AccountModal
        isOpen={showAccountModal}
        onClose={() => {
          setShowAccountModal(false);
          setEditingAccount(null);
        }}
        onSubmit={editingAccount ? handleUpdateAccount : handleCreateAccount}
        account={editingAccount}
        accounts={accounts}
        loading={modalLoading}
      />

      <JournalEntryModal
        isOpen={showJournalModal}
        onClose={() => {
          setShowJournalModal(false);
          setEditingJournalEntry(null);
        }}
        onSubmit={editingJournalEntry ? handleUpdateJournalEntry : handleCreateJournalEntry}
        onApprove={handleApproveJournalEntry}
        journalEntry={editingJournalEntry}
        accounts={accounts}
        loading={modalLoading}
        getNextEntryNumber={getNextEntryNumber}
      />

      {/* Delete Confirmation Modal */}
      <BrandModal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} maxWidthClass="max-w-lg">
        <BrandModalHeader
          title={`Delete ${activeTab === 'accounts' ? 'Account' : 'Journal Entry'}`}
          onClose={() => setShowDeleteModal(false)}
        />
        <BrandModalBody>
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
            <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
          </div>
          <p className="text-sm text-slate-600 text-center">
            Are you sure you want to delete {activeTab === 'accounts' ? 'this account' : 'this journal entry'}? This action cannot be undone.
          </p>
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger text-sm"
            onClick={activeTab === 'accounts' ? handleDeleteAccount : handleDeleteJournalEntry}
          >
            Delete
          </button>
        </BrandModalFooter>
      </BrandModal>

      {/* View Modal */}
      <BrandModal open={showViewModal} onClose={() => setShowViewModal(false)} maxWidthClass="max-w-2xl">
        <BrandModalHeader
          title={activeTab === 'accounts' ? 'Account Details' : 'Journal Entry Details'}
          onClose={() => setShowViewModal(false)}
        />
        <BrandModalBody>
          {activeTab === 'accounts' && selectedAccount && (
            <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-slate-500">Code</dt>
                <dd className="mt-1 text-sm text-slate-900">{selectedAccount.code}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-slate-500">Name</dt>
                <dd className="mt-1 text-sm text-slate-900">{selectedAccount.name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-slate-500">Type</dt>
                <dd className="mt-1">{getAccountTypeBadge(selectedAccount)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-slate-500">Balance</dt>
                <dd className="mt-1 text-sm font-medium text-slate-900">
                  {formatCurrency(selectedAccount.balance)}
                </dd>
              </div>
              {selectedAccount.description && (
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-slate-500">Description</dt>
                  <dd className="mt-1 text-sm text-slate-900">{selectedAccount.description}</dd>
                </div>
              )}
            </dl>
          )}

          {activeTab === 'journal' && selectedJournalEntry && (
            <div className="space-y-4">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-slate-500">Entry Number</dt>
                  <dd className="mt-1 text-sm text-slate-900">{selectedJournalEntry.entryNumber}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Date</dt>
                  <dd className="mt-1 text-sm text-slate-900">
                    {format(new Date(selectedJournalEntry.date), 'MMM dd, yyyy')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Status</dt>
                  <dd className="mt-1">{getStatusBadge(selectedJournalEntry)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Source</dt>
                  <dd className="mt-1">{getSourceBadge(selectedJournalEntry)}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-slate-500">Description</dt>
                  <dd className="mt-1 text-sm text-slate-900">{selectedJournalEntry.description}</dd>
                </div>
                {selectedJournalEntry.reference && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-slate-500">Reference</dt>
                    <dd className="mt-1 text-sm text-slate-900">{selectedJournalEntry.reference}</dd>
                  </div>
                )}
              </dl>

              <div>
                <h4 className="text-sm font-medium text-slate-900 mb-2">Line Items</h4>
                <div className="overflow-hidden border border-gray-200 rounded-md">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-slate-50/80">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Account</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Debit</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Credit</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedJournalEntry.lineItems.map((line, index) => (
                        <tr key={index}>
                          <td className="px-3 py-2 text-sm text-slate-900">{line.accountName}</td>
                          <td className="px-3 py-2 text-sm text-right text-slate-900">
                            {line.debitAmount > 0 ? formatCurrency(line.debitAmount) : ''}
                          </td>
                          <td className="px-3 py-2 text-sm text-right text-slate-900">
                            {line.creditAmount > 0 ? formatCurrency(line.creditAmount) : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50/80">
                      <tr>
                        <td className="px-3 py-2 text-sm font-medium text-slate-900">Total:</td>
                        <td className="px-3 py-2 text-sm text-right font-medium text-slate-900">
                          {formatCurrency(selectedJournalEntry.totalDebit)}
                        </td>
                        <td className="px-3 py-2 text-sm text-right font-medium text-slate-900">
                          {formatCurrency(selectedJournalEntry.totalCredit)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {selectedJournalEntry.notes && (
                <div>
                  <dt className="text-sm font-medium text-slate-500">Notes</dt>
                  <dd className="mt-1 text-sm text-slate-900">{selectedJournalEntry.notes}</dd>
                </div>
              )}
            </div>
          )}
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-primary text-sm"
            onClick={() => setShowViewModal(false)}
          >
            Close
          </button>
        </BrandModalFooter>
      </BrandModal>
    </div>
  );
} 